import React, { Component } from 'react'
import { toast } from 'react-toastify'
import Loader from '../../../components/loader'
import { getString } from '../../../locale'
import {
    clearLogJournal,
    clearPersistedLiveLogs,
    getLogJournal,
    getLoggingEnabled,
    getLiveLogsPersist,
    getPersistedLiveLogs,
    getRemoteLiveLogStream,
    LogJournalEntry,
    PersistedLiveLogEntry,
    setLoggingEnabled,
    setLiveLogsPersist,
    setRemoteLiveLogStream,
} from '../../../api/0x0005/general-settings'
import { onLiveLogNotification, ILiveLogNotification } from '../../../wsclient/handlers/live-log-notification.handler'

type Props = {
    clientId: string
}

type LiveLogEntry = {
    timestampMs: number
    line: string
}

type State = {
    loading: boolean
    enabled: boolean
    loggingEnabled: boolean
    prependTimestamp: boolean
    persistEnabled: boolean
    lines: LiveLogEntry[]
    journalEntries: LogJournalEntry[]
    pageIndex: number
    pageSize: number
    totalEntries: number
    loadingJournal: boolean
    actionInProgress: boolean
}

export default class LiveLogsTab extends Component<Props, State> {
    private unsubscribeLiveLog?: () => void
    private readonly ansiRegex = /\x1B\[[0-?]*[ -/]*[@-~]/g

    state: State = {
        loading: true,
        enabled: false,
        loggingEnabled: false,
        prependTimestamp: false,
        persistEnabled: false,
        lines: [],
        journalEntries: [],
        pageIndex: 0,
        pageSize: 10,
        totalEntries: 0,
        loadingJournal: false,
        actionInProgress: false,
    }

    componentDidMount() {
        this.load()
        this.unsubscribeLiveLog = onLiveLogNotification(this.handleLiveLogNotification)
    }

    componentWillUnmount() {
        if (this.unsubscribeLiveLog) this.unsubscribeLiveLog()
    }

    handleLiveLogNotification = (data: ILiveLogNotification) => {
        if (data.clientId !== this.props.clientId) return
        const line = this.stripAnsi(data.line || '').trim()
        if (!line) return
        this.setState(prev => ({
            enabled: true,
            lines: [...prev.lines.slice(-199), { timestampMs: Date.now(), line }],
        }))
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const pageIndex = this.state.pageIndex
            const pageSize = this.state.pageSize
            const [stream, logging, journal, persist, persisted] = await Promise.all([
                getRemoteLiveLogStream(this.props.clientId),
                getLoggingEnabled(this.props.clientId).catch(() => ({ enabled: 0 })),
                getLogJournal(this.props.clientId, pageIndex, pageSize).catch(() => ({
                    success: 1,
                    pageIndex,
                    pageSize,
                    totalEntries: 0,
                    returnedCount: 0,
                    entries: [] as LogJournalEntry[],
                })),
                getLiveLogsPersist(this.props.clientId).catch(() => ({ enabled: 0 })),
                getPersistedLiveLogs(this.props.clientId).catch(() => ({ entries: [] as PersistedLiveLogEntry[] })),
            ])
            this.setState({
                enabled: stream.enabled === 1,
                loggingEnabled: logging.enabled === 1,
                persistEnabled: persist.enabled === 1,
                lines: (persisted.entries || []).slice(-200).map(entry => ({
                    timestampMs: entry.timestampMs,
                    line: this.stripAnsi(entry.line || '').trim(),
                })),
                journalEntries: journal.entries || [],
                pageIndex: journal.pageIndex ?? pageIndex,
                pageSize: journal.pageSize ?? pageSize,
                totalEntries: journal.totalEntries ?? 0,
                loading: false,
            })
        } catch (error: any) {
            toast.error(error.message || 'Failed to load live logs setting')
            this.setState({
                enabled: false,
                loading: false,
            })
        }
    }

    loadJournalPage = async (pageIndex: number) => {
        this.setState({ loadingJournal: true })
        try {
            const journal = await getLogJournal(this.props.clientId, pageIndex, this.state.pageSize)
            this.setState({
                journalEntries: journal.entries || [],
                pageIndex: journal.pageIndex ?? pageIndex,
                pageSize: journal.pageSize ?? this.state.pageSize,
                totalEntries: journal.totalEntries ?? this.state.totalEntries,
                loadingJournal: false,
            })
        } catch (error: any) {
            toast.error(error.message || 'Failed to load log journal')
            this.setState({ loadingJournal: false })
        }
    }

    formatTimestamp = (timestampMs: number): string => {
        const date = new Date(timestampMs)
        const dd = String(date.getDate()).padStart(2, '0')
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const yyyy = date.getFullYear()
        const hh = String(date.getHours()).padStart(2, '0')
        const min = String(date.getMinutes()).padStart(2, '0')
        const sec = String(date.getSeconds()).padStart(2, '0')
        return `${dd}/${mm}/${yyyy} ${hh}:${min}:${sec}`
    }

    stripAnsi = (text: string): string => text.replace(this.ansiRegex, '')

    getLogLineClass = (line: string): string => {
        const firstChar = line.trim().charAt(0).toUpperCase()
        if (firstChar === 'E') return 'text-red-500'
        if (firstChar === 'W') return 'text-yellow-500'
        if (firstChar === 'I') return 'text-green-500'
        return 'text-gray-800 dark:text-gray-200'
    }

    getJournalLineClass = (level: number): string => {
        if (level === 0) return 'text-red-500'
        if (level === 1) return 'text-yellow-500'
        if (level === 2) return 'text-green-500'
        return 'text-gray-800 dark:text-gray-200'
    }

    togglePersist = async () => {
        const nextEnabled = this.state.persistEnabled ? 0 : 1
        this.setState({ actionInProgress: true })
        try {
            await setLiveLogsPersist(this.props.clientId, nextEnabled)
            this.setState({ persistEnabled: nextEnabled === 1, actionInProgress: false })
        } catch (error: any) {
            toast.error(error.message || 'Failed to update persistence')
            this.setState({ actionInProgress: false })
        }
    }

    clearLogs = async () => {
        this.setState({ actionInProgress: true })
        try {
            await clearPersistedLiveLogs(this.props.clientId)
            this.setState({ lines: [], actionInProgress: false })
        } catch (error: any) {
            toast.error(error.message || 'Failed to clear logs')
            this.setState({ actionInProgress: false })
        }
    }

    toggleLoggingEnabled = async () => {
        const nextEnabled = this.state.loggingEnabled ? 0 : 1
        this.setState({ actionInProgress: true })
        try {
            await setLoggingEnabled(this.props.clientId, nextEnabled)
            this.setState({ loggingEnabled: nextEnabled === 1, actionInProgress: false })
        } catch (error: any) {
            toast.error(error.message || 'Failed to update logging')
            this.setState({ actionInProgress: false })
        }
    }

    toggleRemoteLiveLogStream = async () => {
        const nextEnabled = this.state.enabled ? 0 : 1
        this.setState({ actionInProgress: true })
        try {
            await setRemoteLiveLogStream(this.props.clientId, nextEnabled)
            this.setState({ enabled: nextEnabled === 1, actionInProgress: false })
        } catch (error: any) {
            toast.error(error.message || 'Failed to update live log stream')
            this.setState({ actionInProgress: false })
        }
    }

    clearJournal = async () => {
        if (!window.confirm(getString('general_settings.log_journal_clear_confirm'))) return
        this.setState({ actionInProgress: true })
        try {
            await clearLogJournal(this.props.clientId)
            await this.loadJournalPage(0)
            this.setState({ actionInProgress: false })
        } catch (error: any) {
            toast.error(error.message || 'Failed to clear log journal')
            this.setState({ actionInProgress: false })
        }
    }

    render() {
        if (this.state.loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{getString('general_settings.live_logs_title')}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{getString('general_settings.live_logs_desc')}</p>
                {!this.state.enabled && (
                    <div className="bg-amber-50 dark:bg-gray-800 border border-amber-300 dark:border-amber-400 rounded-lg shadow-sm p-4 text-sm font-medium leading-relaxed text-amber-900 dark:text-white mb-4">
                        {getString('general_settings.remote_live_log_stream_desc')}
                    </div>
                )}
                <div className="mb-4 flex flex-wrap gap-3 items-center">
                    <button
                        onClick={this.toggleRemoteLiveLogStream}
                        disabled={this.state.actionInProgress}
                        className="px-3 py-1.5 rounded bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-sm"
                    >
                        {`${getString('general_settings.remote_live_log_stream')}: ${
                            this.state.enabled ? getString('general_settings.enabled') : getString('general_settings.disabled')
                        }`}
                    </button>
                    <button
                        onClick={this.toggleLoggingEnabled}
                        disabled={this.state.actionInProgress}
                        className="px-3 py-1.5 rounded bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white text-sm"
                    >
                        {`${getString('general_settings.logging_enabled')}: ${
                            this.state.loggingEnabled ? getString('general_settings.enabled') : getString('general_settings.disabled')
                        }`}
                    </button>
                    <label className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                        <input
                            type="checkbox"
                            checked={this.state.prependTimestamp}
                            onChange={event => this.setState({ prependTimestamp: event.target.checked })}
                        />
                        {getString('general_settings.live_logs_prepend_timestamp')}
                    </label>
                    <button
                        onClick={this.togglePersist}
                        disabled={this.state.actionInProgress}
                        className="px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm"
                    >
                        {this.state.persistEnabled ? getString('general_settings.live_logs_disable_persist') : getString('general_settings.live_logs_enable_persist')}
                    </button>
                    <button
                        onClick={this.clearLogs}
                        disabled={this.state.actionInProgress}
                        className="px-3 py-1.5 rounded bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm"
                    >
                        {getString('general_settings.live_logs_clear')}
                    </button>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                    <div
                        className="h-[70vh] overflow-y-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 font-mono text-xs whitespace-pre-wrap text-left"
                        dir="ltr"
                    >
                        {this.state.lines.length === 0 ? (
                            <div className="text-gray-800 dark:text-gray-200">{getString('general_settings.live_logs_empty')}</div>
                        ) : (
                            this.state.lines.map((entry, index) => (
                                <div key={index} className={this.getLogLineClass(entry.line)}>
                                    {this.state.prependTimestamp ? `[${this.formatTimestamp(entry.timestampMs)}] ${entry.line}` : entry.line}
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mt-6">
                    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('general_settings.log_journal_title')}</h2>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                {getString('general_settings.log_journal_desc')}
                                {` (${getString('general_settings.current_value')}: ${this.state.totalEntries})`}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => this.loadJournalPage(this.state.pageIndex)}
                                disabled={this.state.loadingJournal}
                                className="px-3 py-1.5 rounded bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm"
                            >
                                {getString('general_settings.log_journal_refresh')}
                            </button>
                            <button
                                onClick={this.clearJournal}
                                disabled={this.state.actionInProgress}
                                className="px-3 py-1.5 rounded bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm"
                            >
                                {getString('general_settings.log_journal_clear')}
                            </button>
                        </div>
                    </div>
                    <div className="h-[45vh] overflow-y-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 font-mono text-xs whitespace-pre-wrap text-left" dir="ltr">
                        {this.state.journalEntries.length === 0 ? (
                            <div className="text-gray-800 dark:text-gray-200">{getString('general_settings.log_journal_empty')}</div>
                        ) : (
                            this.state.journalEntries.map(entry => (
                                <div key={entry.seq} className={this.getJournalLineClass(entry.level)}>
                                    [{this.formatTimestamp(entry.tsEpochMs)}] [{entry.tag}] {entry.message}
                                </div>
                            ))
                        )}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button
                            onClick={() => this.loadJournalPage(Math.max(0, this.state.pageIndex - 1))}
                            disabled={this.state.loadingJournal || this.state.pageIndex <= 0}
                            className="px-3 py-1.5 rounded bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm"
                        >
                            {getString('common.previous')}
                        </button>
                        <button
                            onClick={() => this.loadJournalPage(this.state.pageIndex + 1)}
                            disabled={this.state.loadingJournal || (this.state.pageIndex + 1) * this.state.pageSize >= this.state.totalEntries}
                            className="px-3 py-1.5 rounded bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white text-sm"
                        >
                            {getString('common.next')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}
